import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Alert, Button, Card, CircularProgress, Grid } from "@mui/material";
import { toast } from "react-toastify";
import { P } from "const/permissions";
import { usePermission } from "hooks/usePermissions";
import { useMappingData } from "./useMappingData";
import { useMappingMutations } from "./useMappingMutations";
import { useImportFields } from "./useImportFields";
import { applyPrefillKeyToField, createMappingField } from "./mappingFieldFactory";
import { FIRST_PAGE_NUMBER } from "./mappingConstants";
import {
  COPY_SOURCES_QUERY_KEY,
  FILES_QUERY_KEY,
  MAPPING_QUERY_KEY,
} from "./documentLibraryQueryKeys";
import { libraryErrorMessage } from "./libraryErrorMessage";
import MappingPageHeader from "./MappingPageHeader";
import MappingPageCanvasPanel from "./MappingPageCanvasPanel";
import MappingFieldList from "./MappingFieldList";
import CopyMappingDialog from "./CopyMappingDialog";
import ImportFieldsDialog from "./ImportFieldsDialog";

/**
 * Dedicated field-mapping screen for one college document: draw rectangles on the page preview,
 * assign each field's type/owner, optionally link it to a prefill key, then save. Route:
 * `/admin/university/:universityId/documents/:fileId/mapping`.
 *
 * Deliberately separate from the generic Document Centre mapping UI (`views/admin/Documents`).
 * That page's endpoints aren't gated per college document, so routing this through its own
 * `university:document:*`-gated pair keeps the permission meaningful here.
 */
const MappingPage = () => {
  const { universityId, fileId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canEdit = usePermission(P.UNIVERSITY_DOCUMENT.UPDATE);

  const { fields, setFields, mappingQuery, prefillQuery, prefillGroups, prefillByKey } =
    useMappingData(fileId);
  const { saveMutation } = useMappingMutations(fileId, fields);

  const [selectedIndex, setSelectedIndex] = useState(null);
  const [activePage, setActivePage] = useState(FIRST_PAGE_NUMBER);
  const [copyOpen, setCopyOpen] = useState(false);

  // Every field is replaced, so whatever was selected no longer refers to the same field.
  const importFields = useImportFields({
    fileId,
    fields,
    setFields,
    onFieldsReplaced: () => setSelectedIndex(null),
  });

  // Fields are addressed by position, never by name: a name may legitimately repeat so one value
  // can be mirrored onto several spots, and matching by name would edit every copy at once.
  const updateField = (index, changes) =>
    setFields((current) =>
      current.map((field, at) => (at === index ? { ...field, ...changes } : field))
    );

  const handleAddField = () => {
    const created = createMappingField(fields, activePage);
    setFields((current) => [...current, created]);
    setSelectedIndex(fields.length);
  };

  const handleDeleteField = (index) => {
    setFields((current) => current.filter((field, at) => at !== index));
    // Later fields shift down by one, so a selection past the removed row has to shift with them.
    setSelectedIndex((current) => {
      if (current === null) return null;
      if (current === index) return null;
      return current > index ? current - 1 : current;
    });
  };

  const handlePrefillChange = (index, prefillKey) =>
    setFields((current) =>
      current.map((field, at) =>
        at === index ? applyPrefillKeyToField(field, prefillKey, prefillByKey) : field
      )
    );

  const handleRectangleDrawn = (rect) => {
    if (selectedIndex === null) {
      toast.info("Pick a field first, then draw its box.");
      return;
    }
    updateField(selectedIndex, rect);
  };

  const handleCopied = () => {
    queryClient.invalidateQueries({ queryKey: [MAPPING_QUERY_KEY, fileId] });
    // A copy persists server-side immediately, so the library's mappedFieldCount is already stale.
    // Without this the file still reads "Not mapped" and its Send button stays disabled, and the
    // global refetchOnMount:false means simply navigating back will not correct it.
    queryClient.invalidateQueries({ queryKey: [FILES_QUERY_KEY] });
    queryClient.invalidateQueries({ queryKey: [COPY_SOURCES_QUERY_KEY] });
  };

  const goBack = () => navigate(`/admin/university/${universityId}/documents`);

  if (mappingQuery.isLoading) {
    return (
      <Card sx={{ p: 4, display: "flex", justifyContent: "center" }}>
        <CircularProgress size={28} />
      </Card>
    );
  }

  if (mappingQuery.isError) {
    return (
      <Card sx={{ p: 2 }}>
        <Alert severity="error" action={<Button onClick={goBack}>Back</Button>}>
          {libraryErrorMessage(mappingQuery.error, "Could not load this document's mapping")}
        </Alert>
      </Card>
    );
  }

  const previews = mappingQuery.data?.previewImageUrls || [];

  return (
    <Card sx={{ p: 2 }}>
      <MappingPageHeader
        fileName={mappingQuery.data?.name}
        canEdit={canEdit}
        saving={saveMutation.isLoading}
        importing={importFields.importing}
        onBack={goBack}
        onImportFields={importFields.requestImport}
        onCopyMapping={() => setCopyOpen(true)}
        onSave={() => saveMutation.mutate()}
      />

      {prefillQuery.isError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {libraryErrorMessage(prefillQuery.error, "Could not load the linked-detail catalog")}.
          Auto-fill links are unavailable until this loads.
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <MappingPageCanvasPanel
            previews={previews}
            activePage={activePage}
            onActivePageChange={setActivePage}
            fields={fields}
            selectedIndex={selectedIndex}
            onSelectField={setSelectedIndex}
            canEdit={canEdit}
            onRectangleDrawn={handleRectangleDrawn}
          />
        </Grid>

        <Grid item xs={12} md={5}>
          <MappingFieldList
            fields={fields}
            selectedIndex={selectedIndex}
            canEdit={canEdit}
            prefillGroups={prefillGroups}
            onSelect={setSelectedIndex}
            onUpdate={updateField}
            onPrefillChange={handlePrefillChange}
            onDelete={handleDeleteField}
            onAdd={handleAddField}
          />
        </Grid>
      </Grid>

      <ImportFieldsDialog
        open={importFields.confirmOpen}
        replacedFieldCount={importFields.replacedFieldCount}
        onCancel={importFields.cancelImport}
        onConfirm={importFields.confirmImport}
      />

      <CopyMappingDialog
        open={copyOpen}
        fileId={fileId}
        onClose={() => setCopyOpen(false)}
        onCopied={handleCopied}
      />
    </Card>
  );
};

export default MappingPage;
